import { Component, HostListener, Inject, LOCALE_ID, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';

import { TranslateService } from '@ngx-translate/core';

import { ProductsFacade, PurchaseFacade } from '@ridango/components';
import { AppStartupService, keyboard, ListService, Product } from '@ridango/core';

import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import Env from '@ridango/core/env';

@Component({
    selector: 'rwidget-wallet-product-selector',
    templateUrl: 'wallet-product-selector.component.html'
})
export class WalletProductSelectorComponent implements OnInit, OnDestroy {
    private componentDestroyed$ = new Subject<void>();
    private amountValidators: any[];
    private isMobileView = false;
    private focusedItem = null;
    // private variableSumProduct: Product;
    private pattern = this.env?.AMOUNT_VALIDATORS?.PATTERN ?? '^\\d+$';
    products$ = this.productsFacade.all$;
    fixedSumProducts$ = this.productsFacade.fixedSumProducts$;
    variableSumProduct$ = this.productsFacade.variableSumProduct$;
    selectedProduct$ = this.productsFacade.selected$;

    activeTab = 1;
    // TODO: this is here for deadline consideration only, must use state, when have time to redo
    selectedProduct: Product;
    // fixedSumProducts: Product[];
    amountField: FormControl;
    sumValid = true;

    @ViewChildren('radioItem') radioItemEls: QueryList<any>;
    @ViewChildren('radioItemMobile') radioItemMobileEls: QueryList<any>;

    constructor(
        @Inject(LOCALE_ID) public locale: string,
        @Inject('ENV') public env: Env,
        private productsFacade: ProductsFacade,
        public translate: TranslateService,
        private listService: ListService,
        private purchaseFacade: PurchaseFacade,
        public appStartupService: AppStartupService
    ) {}

    ngOnInit() {
        this.locale = this.translate.currentLang;

        this.amountValidators = [
            Validators.required,
            Validators.pattern(this.pattern),
            Validators.minLength(this.env?.AMOUNT_VALIDATORS?.MIN_LENGTH ?? 1),
            Validators.maxLength(this.env?.AMOUNT_VALIDATORS?.MAX_LENGTH ?? 4)
        ];
        this.amountField = new FormControl('', this.amountValidators);

        // set selected if only one product is available
        this.products$
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((products) => products.length === 1 && this.setSelectedProduct(products[0]));

        this.productsFacade.variableSumProduct$
            .pipe(takeUntil(this.componentDestroyed$))
            .subscribe((product: Product) => {
                if (product) {
                    this.amountValidators = this.setMinAndMax(product);
                } else {
                    this.amountValidators = null;
                }
            });
        // check if mobile view
        this.setView();
    }

    ngOnDestroy() {
        this.componentDestroyed$.next();
        this.componentDestroyed$.complete();
    }

    @HostListener('window:resize', ['$event']) onResize() {
        this.setView();
    }

    private setMinAndMax(product: Product) {
        const newAmountValidators = this.amountValidators.concat([
            Validators.min(product.product_properties.find((p) => p.code === 'LOAD_WALLET_MIN').value),
            Validators.max(product.product_properties.find((p) => p.code === 'LOAD_WALLET_MAX').value)
        ]);
        this.amountField.setValidators(newAmountValidators);
        return newAmountValidators;
    }

    private setView() {
        this.isMobileView = window.innerWidth <= 1030;
    }

    setSelectedProduct(product: Product) {
        this.productsFacade.setSelectedProduct(product);
        // TODO: change this when have time, currently due to time constraints should be fine
        this.selectedProduct = product;
        if (product && product.price && product.price > 0) {
            this.purchaseFacade.removeWalletProductSum();
            this.amountField.reset();
        }
    }

    setSum(sum: number, moveFocus = false) {
        if (this.selectedProduct && this.selectedProduct.price && !sum) {
            this.amountField.markAsUntouched();
            return;
        }
        this.amountField.markAsTouched();

        let value = sum.toString();
        value = value.replace(/,/g, '.');
        value = value.replace(/[^0-9.]/g, '');
        value = value.replace(/(\..*)\./g, '$1');
        value = value.replace(/^(\d*\.\d{2}).*$/, '$1');

        sum = parseFloat(value) || 0;

        this.variableSumProduct$.pipe(take(1)).subscribe((product) => {
            this.setSelectedProduct(product);
        });

        if (this.validateEnteredSum(this.selectedProduct, sum.toString())) {
            /* this.ticketsService.setPrice(this.ticketUid, this.sum); */
            this.purchaseFacade.setWalletProductSum(sum);
            if (moveFocus) {
                // ME: you should emit event so that parent can send focus instruction to purchase medium widget
                /* if (this.allCards.length > 0) {
                    setTimeout(() => this.purchaseMediumContainerEl.cardInputWithRelatedCardsEl.nativeElement.focus());
                } else {
                    setTimeout(() => this.purchaseMediumContainerEl.cardInputEl.nativeElement.focus());
                } */
            }
            return;
        }
    }

    private validateEnteredSum(product: Product, sum: string): boolean {
        if (!product || !product.product_properties) {
            return false;
        }

        const min = product.product_properties.find((p) => p.code === 'LOAD_WALLET_MIN');
        const max = product.product_properties.find((p) => p.code === 'LOAD_WALLET_MAX');

        const pattern = new RegExp(this.pattern);
        if (min && max && sum && !isNaN(+sum) && pattern.test(sum) && +sum >= min.value && +sum <= max.value) {
            return true;
        }
        if (
            product.product_properties.filter((p) => p.code === 'LOAD_WALLET_MIN').length === 0 &&
            product.product_properties.filter((p) => p.code === 'LOAD_WALLET_MAX').length === 0
        ) {
            return true;
        }

        return false;
    }

    /**
     * Interactions and accessibility
     */

    clickTab(tab: number) {
        this.activeTab = tab;
    }

    getTabIndex(product: Product) {
        // TODO: fix this. Why is this required Adeniyi maybe can answer better?
        // if (this.selectedProduct) {
        //     if (this.selectedProduct.product_id === product.product_id) {
        //         // Selected item
        //         return 0;
        //     }
        // }
        // if (this.fixedSumProducts[0].product_id === product.product_id) {
        //     // First item
        //     return 0;
        // }
        // return -1;
    }

    selectLeftProduct() {
        this.selectNextProduct(-1);
    }

    selectRightProduct() {
        this.selectNextProduct(1);
    }

    private selectNextProduct(direction: number) {
        // TODO: fix this Why is this required Adeniyi maybe can answer better?
        // const currentProduct = this.selectedProduct ? this.selectedProduct : this.focusedItem.item;
        // const selectedIndex = this.fixedSumProducts.findIndex(
        //     (product) => product.product_id === currentProduct.product_id
        // );
        // const nextIndex = this.listService.getNextCyclicIndex(selectedIndex, direction, this.fixedSumProducts.length);
        // this.setSelectedProduct(this.fixedSumProducts[nextIndex]);
        // if (this.isMobileView) {
        //     this.radioItemMobileEls.toArray()[nextIndex].selectCheckItem.nativeElement.focus();
        // } else {
        //     this.radioItemEls.toArray()[nextIndex].selectCheckItem.nativeElement.focus();
        // }
    }

    keydownFromTab(event: KeyboardEvent, tabId: number) {
        switch (event.code) {
            case keyboard.ARROW_RIGHT:
            case keyboard.ARROW_LEFT:
                this.activeTab = tabId === 1 ? 2 : 1;
                break;
            case keyboard.HOME:
                this.activeTab = 1;
                break;
            case keyboard.END:
                this.activeTab = 2;
                break;
        }
    }

    focus(p: Product) {
        const elementIndex = this.findElementIndex(p);
        const radioEls = this.isMobileView ? this.radioItemMobileEls : this.radioItemEls;

        this.focusedItem = radioEls.toArray()[elementIndex];
        this.focusedItem.selectCheckItem.nativeElement.classList.add('focus');
    }

    blur(p: Product) {
        const elementIndex = this.findElementIndex(p);
        const radioEls = this.isMobileView ? this.radioItemMobileEls : this.radioItemEls;

        radioEls.toArray()[elementIndex].selectCheckItem.nativeElement.classList.remove('focus');
        this.focusedItem = null;
    }

    private findElementIndex(p: Product) {
        // TODO: fix this, not sure if its needed
        this.fixedSumProducts$.pipe(take(1)).subscribe((products) => {
            return products.findIndex((fsp: Product) => fsp.product_id === p.product_id);
        });
        return 0;
    }

    getVariableSumMin(product: Product): number {
        return +product.product_properties.find((p) => p.code === 'LOAD_WALLET_MIN').value;
    }
    getVariableSumMax(product: Product): number {
        return +product.product_properties.find((p) => p.code === 'LOAD_WALLET_MAX').value;
    }
}
